import { createTheme } from "@dash/default-theme";
import { Theme, ThemeProvider } from "@emotion/react";
import { NewRouterBreadcrumbsBar } from "@legacy-megarax/ui-components";
import { TradeRouteDetailsContainer, TradeRoutesHomeContainer } from "@megarax/crm-customer-map";
import { AddCustomerPage, CustomerPage, CustomersViewPage } from "@megarax/crm-customers";
import { MyAffiliateCodePage, MyAffiliateCodesPage } from "@megarax/crm-my-affiliates-codes";
import { PriceReportFormContainer } from "@megarax/crm-my-price-reports";
import { OrderDraftPage, OrderDraftsPage, OrderPage, OrdersPage } from "@megarax/crm-orders";
import { PostSaleDiscountsPage } from "@megarax/crm-post-sale-discounts";
import { PriceListDetailsContainer, PriceListHomeContainer } from "@megarax/crm-price-lists";
import { SubjectsViewContainer } from "@megarax/crm-price-reports";
import { ProductAvailabilityHome } from "@megarax/crm-product-availability";
import { ProfitTargetsPage } from "@megarax/crm-profit-targets";
import { RegionPage, RegionRedirect, RegionsHome, SalespersonRedirect } from "@megarax/crm-regions";
import {
  ExportBcItemsPage,
  ExportItemsPage,
  ExportShopifyProductsPage,
  ExportShopifyVariantsPage,
  ImportItemsPage,
  NewTradeItemPage,
  TradeItemPage,
  TradeItemsPage,
} from "@megarax/crm-trade-items";
import { TrainingHome } from "@megarax/crm-training";
import { UserHomepage } from "@megarax/crm-user";
import {
  DayViewContainer,
  MyVisitsRedirect,
  VisitRedirect,
  VisitsRedirect,
  WeekViewContainer,
} from "@megarax/crm-visits";
import { useConfig } from "@megarax/dev-config";
import { appLinkTreeToNavEntries, MegaraxWebappContainer } from "@megarax/features";
import { filterLinkTree } from "@megarax/home";
import { HomeView, MegaraxProviders } from "@megarax/root";
import { MdGroups } from "react-icons/md";
import { Navigate, Outlet, Route } from "react-router-dom-v5-compat";

import { LinkDoc } from "@megaron/docs-contracts";

import { SupportSettingsPage } from "./support/SupportSettingsPage";
import { SupportTicketPage } from "./support/SupportTicketPage";
import { SupportTicketsPage } from "./support/SupportTicketsPage";

const crmTheme: Theme = createTheme("273");

type Props = {
  linkDocs: LinkDoc[] | undefined;
};

export const useCrmRouter = ({ linkDocs }: Props) => {
  return (
    <Route
      path="/crm"
      element={
        <ThemeProvider theme={crmTheme}>
          <CrmMegaraxWebappContainer linkDocs={linkDocs} />
        </ThemeProvider>
      }
    >
      <Route path="customers/*">
        <Route path="*" element={<Navigate to="/crm/customers/new-list" replace />} />
        <Route path=":viewId/*" element={<CustomersViewPage />} />
        <Route path="new/*" element={<AddCustomerPage />} />
        <Route path="id/:customerId/*" element={<CustomerPage />} />
      </Route>
      <Route path="regions/*">
        <Route path="*" element={<RegionsHome />} />
        <Route path="region/:region/*" element={<RegionPage />} />
      </Route>
      <Route path="product-availability/*">
        <Route path="*" element={<ProductAvailabilityHome />} />
      </Route>
      <Route path="training/*">
        <Route path="*" element={<TrainingHome />} />
      </Route>
      <Route path="items">
        <Route path="" element={<TradeItemsPage />} />
        <Route path="new" element={<NewTradeItemPage />} />
        <Route path="import" element={<ImportItemsPage />} />
        <Route path="export" element={<ExportItemsPage />} />
        <Route path="exportBcItems" element={<ExportBcItemsPage />} />
        <Route path="exportShopifyProducts" element={<ExportShopifyProductsPage />} />
        <Route path="exportShopifyVariants" element={<ExportShopifyVariantsPage />} />
        <Route path=":itemId/*" element={<TradeItemPage />} />
      </Route>
      <Route path="post-sale-discounts" element={<PostSaleDiscountsPage />} />
      <Route path="profit-targets" element={<ProfitTargetsPage />} />
      <Route path="region-profit-metrics">
        <Route path="" element={<Navigate to="/crm/regions" replace />} />
        <Route path=":region" element={<RegionRedirect />} />
      </Route>
      <Route path="zgloszenia">
        <Route path="" element={<SupportTicketsPage basePath="/crm/zgloszenia" settingsPath="/crm/zgloszenia/settings" />} />
        <Route path="settings" element={<SupportSettingsPage />} />
        <Route path=":id" element={<SupportTicketPage listPath="/crm/zgloszenia" />} />
      </Route>
      <Route path="user-home" element={<UserHomepage />} />
      <Route path="orders">
        <Route path="" element={<OrdersPage />} />
        <Route path=":number" element={<OrderPage />} />
      </Route>
      <Route path="order-drafts">
        <Route path="" element={<OrderDraftsPage />} />
        <Route path="new/*" element={<OrderDraftPage isNew />} />
        <Route path=":orderId/*" element={<OrderDraftPage />} />
      </Route>
      <Route path="my-affiliate-codes">
        <Route path="" element={<MyAffiliateCodesPage />} />
        <Route path=":affiliateCodeId/*" element={<MyAffiliateCodePage />} />
      </Route>

      {/* Crm (POS) */}
      <Route path="pos" element={<PosCrmMegaraxWebappContainer />}>
        <Route path="" element={<HomeView />} />
        <Route path="cenniki">
          <Route path="" element={<PriceListHomeContainer />}></Route>
          <Route path="nowy" element={<PriceListHomeContainer />}></Route>
          <Route path=":uuid/:tab/*" element={<PriceListDetailsContainer />}></Route>
        </Route>
        <Route path="moje_ankiety_cenowe" element={<PriceReportFormContainer />} />
        <Route path="moje_wizyty" element={<MyVisitsRedirect />} />
        <Route path="handlowcy">
          <Route path={`:userId`}>
            <Route path="" element={<SalespersonRedirect />} />
            <Route path="wizyty">
              <Route path="" element={<VisitsRedirect />} />
              <Route path="tydzien">
                <Route path="" element={<VisitsRedirect />} />
                <Route path=":date/wizyta/:uuid" element={<VisitRedirect />} />
                <Route path=":date/*" element={<WeekViewContainer />} />
              </Route>
              <Route path="dzien">
                <Route path="" element={<VisitsRedirect />} />
                <Route path=":date/wizyta/:uuid" element={<VisitRedirect />} />
                <Route path=":date/*" element={<DayViewContainer />} />
              </Route>
            </Route>
          </Route>
          <Route path="" element={<Navigate to="/crm/regions" replace />} />
        </Route>
        <Route path="trasy">
          <Route path="" element={<TradeRoutesHomeContainer />} />
          <Route path=":tradeRouteUuid" element={<TradeRouteDetailsContainer />}></Route>
        </Route>
        <Route path="ankiety_cenowe">
          <Route path="" element={<Navigate to="/crm/pos/ankiety_cenowe/produkty" />} />
          <Route path="produkty" element={<SubjectsViewContainer />} />
        </Route>
      </Route>
    </Route>
  );
};

const CrmMegaraxWebappContainer: React.FC<{
  linkDocs: LinkDoc[] | undefined;
}> = ({ linkDocs }) => {
  const fullLinkTree = filterLinkTree(linkDocs || []);
  const crmLinkTree = fullLinkTree.find((appTree) => appTree.app === "crm");

  const { config } = useConfig();

  return (
    <MegaraxWebappContainer
      logo={<MdGroups width="1em" height="1em" />}
      title="CRM"
      app="crm"
      subtitle="megarax.net"
      bypassAuth={config.testUser.enabled}
      navEntries={appLinkTreeToNavEntries(crmLinkTree)}
    >
      <MegaraxProviders>
        <Outlet />
      </MegaraxProviders>
    </MegaraxWebappContainer>
  );
};

const PosCrmMegaraxWebappContainer = () => {
  return (
    <MegaraxProviders>
      <NewRouterBreadcrumbsBar />
      <Outlet />
    </MegaraxProviders>
  );
};
